/**
 * Eine Bestellung anlegen oder bearbeiten – dasselbe Formular für beides.
 *
 * Zwei Pflichtfelder: Lieferant, Bestellumfang. Ohne sie
 * bleibt „Bestellung anlegen" gesperrt, das leere Feld ist rot umrandet, und
 * ein Satz darunter sagt, was fehlt. Verantwortlich ist der Kalkulant des
 * Angebots – für jede Bestellung, ohne Auswahl. Der Liefertermin ist
 * hier freiwillig: Er steht erst in der unterfertigten AB, meist als
 * Kalenderwoche, und wird beim Schritt „AB retour" verlangt.
 *
 * Beim Bearbeiten steht jede Änderung mit Vorher und Nachher in der Chronik.
 * Schritte werden hier nicht verändert; dafür gibt es den roten Knopf.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer, Pflicht, PflichtLegende } from '../komponenten/Modal';
import type { AngebotAntwort, Bestellung } from '../typen';

export function BestellungModal({
  bestellung,
  angebot,
  onSchliessen,
  onFertig,
}: {
  /** null: eine neue Bestellung anlegen. */
  bestellung: Bestellung | null;
  angebot: { id: number; name: string; kalkulant: string };
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const meta = useMeta();
  const neu = bestellung === null;
  const [lieferant, setLieferant] = useState(bestellung?.lieferant ?? '');
  const [umfang, setUmfang] = useState(bestellung?.umfang ?? '');
  const [lieferWoche, setLieferWoche] = useState(bestellung?.lieferWoche ?? '');
  const [lieferDatum, setLieferDatum] = useState(bestellung?.lieferDatum ?? '');
  const [fehler, setFehler] = useState('');
  const [zurueckFrage, setZurueckFrage] = useState(false);

  /* Was fehlt – in der Reihenfolge der Felder. */
  const fehlt: string[] = [];
  if (!lieferant.trim()) fehlt.push('Lieferant');
  if (!umfang.trim()) fehlt.push('Bestellumfang');

  /*
   * Nur für Verklicker, deshalb versteckt am Ende und nur am selben Tag: Der
   * letzte quittierte Schritt wird entfernt, die Bestellung steht wieder auf
   * dem davor. Die Rücknahme selbst steht in der Chronik.
   */
  const letzter = bestellung?.eintraege[0];
  const zurueckMoeglich = !!letzter && letzter.datum === meta.heute;
  const zurueck = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/abwicklung/bestellungen/${bestellung!.id}/schritt-zurueck`),
    onSuccess: (a) => {
      onFertig();
      toastZeigen(a.toast ?? 'Zurückgenommen.');
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Zurücknehmen fehlgeschlagen.'),
  });

  /* Beim Bearbeiten nur senden, was sich geändert hat – sonst schriebe die Chronik Leerläufe. */
  const daten: Record<string, string | null> = {};
  if (bestellung) {
    if (lieferant.trim() !== bestellung.lieferant) daten.lieferant = lieferant.trim();
    if (umfang.trim() !== bestellung.umfang) daten.umfang = umfang.trim();
    if ((lieferWoche || null) !== bestellung.lieferWoche) daten.lieferWoche = lieferWoche || null;
    if ((lieferDatum || null) !== bestellung.lieferDatum) daten.lieferDatum = lieferDatum || null;
  }
  const geaendert = Object.keys(daten).length > 0;

  const mutation = useMutation({
    mutationFn: () =>
      neu
        ? api.sende<AngebotAntwort>(`/abwicklung/angebote/${angebot.id}/bestellungen`, {
            lieferant: lieferant.trim(),
            umfang: umfang.trim(),
            lieferWoche: lieferWoche || null,
            lieferDatum: lieferDatum || null,
          })
        : api.aendere<AngebotAntwort>(`/abwicklung/bestellungen/${bestellung.id}`, daten),
    onSuccess: (a) => {
      onFertig();
      toastZeigen(
        neu
          ? (a.toast ?? 'Bestellung angelegt.')
          : 'Bestellung geändert – Vorher und Nachher stehen in der Chronik.',
      );
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  const rot = (feld: string) => (fehlt.includes(feld) ? 'modal-eingabe--fehler' : '');
  const speicherbar = fehlt.length === 0 && (neu || geaendert);

  return (
    <Modal
      titel={neu ? 'Bestellung anlegen' : 'Bestellung bearbeiten'}
      untertitel={
        neu
          ? angebot.name
          : `${angebot.name} · Bestellung ${bestellung.nr}${bestellung.lieferant ? ` · ${bestellung.lieferant}` : ''}`
      }
      breite={540}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={() => mutation.mutate()} disabled={mutation.isPending || !speicherbar}>
            {mutation.isPending ? 'Wird gespeichert …' : neu ? 'Bestellung anlegen' : 'Speichern'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>Lieferant<Pflicht /></span>
        <input
          className={`modal-eingabe ${rot('Lieferant')}`}
          value={lieferant}
          placeholder="z. B. Renson"
          autoFocus={neu}
          onChange={(e) => {
            setLieferant(e.target.value);
            setFehler('');
          }}
        />
      </label>

      <label className="modal-feld">
        <span>Bestellumfang<Pflicht /></span>
        <input
          className={`modal-eingabe ${rot('Bestellumfang')}`}
          value={umfang}
          placeholder="z. B. Camargue Skye 6 × 4 m · Zip-Screens 3 Seiten · RAL 7016"
          onChange={(e) => {
            setUmfang(e.target.value);
            setFehler('');
          }}
        />
      </label>

      {/*
        Bei wem die Bestellung liegt, entscheidet die Zuständigkeitsmatrix je
        Schritt (Mein Tag v6): Sie beginnt bei „Aufmaß / Klärung" und damit
        beim Kalkulanten des Angebots. Hier wird niemand gewählt – wer sie
        weitergeben will, tut das auf der Karte mit Begründung.
      */}
      <div className="modal-feld">
        <span>Liegt bei · laut Zuständigkeiten</span>
        <div className="modal-festwert">{angebot.kalkulant} · Kalkulant · Aufmaß / Klärung</div>
      </div>

      <div className="modal-feld">
        <span>Liefertermin · optional · spätestens mit der AB retour, meist als Kalenderwoche</span>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label className="modal-feld" style={{ flex: 1, minWidth: 160, marginTop: 0 }}>
            <span style={{ fontSize: 12 }}>Kalenderwoche</span>
            <input
              type="week"
              className="modal-eingabe"
              value={lieferWoche}
              onChange={(e) => {
                setLieferWoche(e.target.value);
                setFehler('');
              }}
            />
          </label>
          {!neu && (
            <label className="modal-feld" style={{ flex: 1, minWidth: 160, marginTop: 0 }}>
              <span style={{ fontSize: 12 }}>Bestätigtes Datum</span>
              <input
                type="date"
                className="modal-eingabe"
                value={lieferDatum}
                onChange={(e) => {
                  setLieferDatum(e.target.value);
                  setFehler('');
                }}
              />
            </label>
          )}
        </div>
      </div>

      {fehlt.length > 0 && (
        <div className="modal-hinweis" style={{ color: 'var(--eh-rot)', fontWeight: 600 }}>
          Es fehlt noch: {fehlt.join(', ')}.
        </div>
      )}
      <PflichtLegende />

      {!neu && (
        <div className="modal-hinweis">
          Der Schritt selbst lässt sich hier nicht ändern – dafür gibt es den roten Knopf auf der
          Karte. Jede Änderung hier steht mit Vorher und Nachher in der Chronik.
        </div>
      )}

      {zurueckMoeglich && !zurueckFrage && (
        <button
          type="button"
          className="modal-textlink modal-textlink--leise"
          onClick={() => setZurueckFrage(true)}
        >
          Verklickt? Letzten Schritt zurücknehmen …
        </button>
      )}
      {zurueckFrage && letzter && (
        <div className="modal-warnkasten">
          <span>
            „{letzter.schrittLang}" vom {letzter.datumText} wird zurückgenommen; die Bestellung steht
            danach wieder auf dem Schritt davor. Das steht in der Chronik.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <KnopfSekundaer onClick={() => setZurueckFrage(false)} disabled={zurueck.isPending}>
              Doch nicht
            </KnopfSekundaer>
            <KnopfPrimaer onClick={() => zurueck.mutate()} disabled={zurueck.isPending}>
              {zurueck.isPending ? 'Wird zurückgenommen …' : 'Zurücknehmen'}
            </KnopfPrimaer>
          </div>
        </div>
      )}

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
